<template>
    <b-button
        :id="btnId"
        class="submit-button"
        :class="[
            `state-${state} ${invertColors ? 'invert-colors border-0' : ''}`,
            { 'no-styling': this.noStyle },
        ]"
        :tabindex="tabindex"
        :disabled="isDisabled && !modalVisible"
        :style="{ opacity: modalVisible ? 1 : undefined }"
        :variant="currentVariant"
        :size="size"
        @click.prevent.stop="onClick"
    >
        <span class="label success">
            <slot name="success-label">
                <icon icon="fa-solid fa-check" :scale="iconScale" />
            </slot>
            <div v-if="successAlert != ''" role="alert" class="success-alert">
                {{ successAlert }}
            </div>
        </span>
        <span class="label warning">
            <slot name="warning-label">
                <icon icon="fa-solid fa-triangle-exclamation" :scale="iconScale" />
            </slot>
        </span>
        <span class="label error">
            <slot name="error-label">
                <icon icon="fa-solid fa-xmark" :scale="iconScale" />
            </slot>
        </span>
        <span class="label pending">
            <slot name="pending-label">
                <loader :scale="iconScale" />
            </slot>
        </span>
        <span class="label default">
            <slot>
                {{ label }}
            </slot>
        </span>

        <b-popover
            v-if="Maybe.isMaybe(disabled)"
            :id="disabledId"
            :placement="popoverPlacement"
            :container="innerContainer"
            :target="btnId"
            triggers=""
            variant="danger"
            @shown="$refs.disabledPopover.focus()"
            @hidden="onHideDisabled"
        >
            <div
                class="submit-button-disabled"
                ref="disabledPopover"
                tabindex="-1"
                style="outline: 0"
                @blur.capture="hideDisabled"
            >
                <div @click="hideDisabled()">
                    <icon icon="fa-solid fa-xmark" class="hide-button" />
                </div>

                {{ disabled.extract() }}
            </div>
        </b-popover>

        <b-popover
            :id="`${btnId}-error-popover`"
            :placement="popoverPlacement"
            :show="!!error"
            v-if="!mounting"
            :container="innerContainer"
            :boundary="boundary"
            :target="btnId"
            triggers=""
            variant="danger"
            @shown="$refs.errorPopover.focus()"
            @hidden="onHideError"
        >
            <div
                class="submit-button-error"
                ref="errorPopover"
                tabindex="-1"
                style="outline: 0"
                @blur.capture="hideError"
            >
                <div @click="hideError()">
                    <icon icon="fa-solid fa-xmark" class="hide-button" />
                </div>

                <span v-if="error">
                    <slot name="error" :error="error">
                        {{ stringifiedError }}
                    </slot>
                </span>
            </div>
        </b-popover>

        <b-popover
            :id="`${btnId}-warning-popover`"
            :placement="popoverPlacement"
            :show="!!warning"
            :container="innerContainer"
            :boundary="boundary"
            v-if="!mounting"
            :target="btnId"
            triggers=""
            variant="warning"
            @shown="$refs.warningPopover.focus()"
            @hidden="onHideWarning"
        >
            <div
                class="submit-button-warning"
                ref="warningPopover"
                tabindex="-1"
                style="outline: 0"
                @blur.capture="hideWarning"
            >
                <div @click="hideWarning()">
                    <icon icon="fa-solid fa-xmark" class="hide-button" />
                </div>

                <span v-if="warning && warning.messages.length > 0">
                    <slot name="warning" :warning="warning">
                        <template v-if="isWarningHeader(warning) && warning.messages.length > 1">
                            <ul class="p-0 text-left pl-3 m-0">
                                <li v-for="w in warning.messages">{{ w.text }}</li>
                            </ul>
                        </template>
                        <template v-else-if="isWarningHeader(warning)">
                            {{ warning.messages[0].text }}
                        </template>
                        <template v-else>
                            {{ warning.messages }}
                        </template>
                    </slot>
                </span>
            </div>
        </b-popover>

        <template>
            <b-modal
                v-if="confirmInModal"
                style="pointer: initial"
                class="submit-button-confirm-modal"
                dialog-class="push-down"
                title="Are you sure?"
                @hide="$nextTick(() => resetConfirm(true))"
                :visible="confirmVisible"
                :id="`${btnId}-modal`"
            >
                <slot name="confirm">
                    <div class="text-left text-wrap">
                        {{ confirm }}
                    </div>
                </slot>

                <template slot="modal-footer">
                    <div class="w-100 d-flex justify-content-between">
                        <b-btn variant="outline-danger" @click="acceptConfirm"> Confirm </b-btn>
                        <b-btn variant="primary" @click="resetConfirm(true)"> Cancel </b-btn>
                    </div>
                </template>
            </b-modal>

            <b-popover
                v-else-if="confirm && confirm.length > 0"
                :no-fade="duration < 100"
                :id="`${btnId}-confirm-popover`"
                :placement="popoverPlacement"
                :container="innerContainer"
                :boundary="boundary"
                :show="confirmVisible"
                :target="btnId"
                triggers=""
                @hide="resetConfirm(true)"
            >
                <div class="submit-button-confirm text-justify">
                    <slot name="confirm">
                        <p class="confirm-message" role="alert" aria-assertive="true">
                            {{ confirm }}
                        </p>
                    </slot>

                    <b-button-toolbar justify class="mt-2">
                        <b-button
                            size="sm"
                            variant="outline-primary"
                            class="confirm-button confirm-button-accept mr-2"
                            @click="acceptConfirm"
                            tabindex="0"
                        >
                            Yes
                        </b-button>
                        <div class="sep" />
                        <b-button
                            size="sm"
                            variant="primary"
                            class="confirm-button confirm-button-reject"
                            @click="resetConfirm(true)"
                            ref="popover-decline-accept"
                            tabindex="0"
                        >
                            No
                        </b-button>
                    </b-button-toolbar>
                </div>
            </b-popover>
        </template>
    </b-button>
</template>

<script>
import { Either } from 'purify-ts/Either';
import { Maybe } from 'purify-ts/Maybe';

import * as api from '@codegrade/api';

import Icon from '@/components/Icon';
import * as utils from '@/utils';

import Loader from '../Loader';
import { SubmitButtonCancelled, SubmitButtonRetry, SubmitButtonWarning } from './types';

// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
export { SubmitButtonWarning, SubmitButtonCancelled } from './types';

// eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
function hasWarning(el) {
    if (!el) {
        return false;
    }
    if (el.warnings instanceof api.WarningHeader) {
        return el.warnings.messages.length > 0;
    }
    return el.headers && el.headers.warning;
}

let i = 0;

export default {
    name: 'submit-button',

    props: {
        label: {
            type: String,
            default: 'Submit',
        },
        submit: {
            type: Function,
            required: true,
        },
        filterSuccess: {
            type: Function,
            default: x => x,
        },
        filterError: {
            type: Function,
            default: x => {
                throw x;
            },
        },
        id: {
            type: String,
            default: null,
        },
        tabindex: {
            type: [Number, String],
            default: 0,
        },
        // Disabled can be a boolean or a Maybe<string> containing the reason
        // why the button is disabled.
        disabled: {
            type: [Boolean, Object],
            default: false,
        },
        size: {
            type: String,
            default: 'md',
        },
        variant: {
            type: String,
            default: 'primary',
        },
        duration: {
            type: Number,
            default: 750,
        },
        confirm: {
            type: String,
            default: '',
        },
        confirmInModal: {
            type: Boolean,
            default: false,
        },
        popoverPlacement: {
            type: String,
            default: 'top',
        },
        waitAtLeast: {
            type: Number,
            default: 250,
        },
        iconScale: {
            type: Number,
            default: 1,
        },
        container: {
            default: undefined,
        },
        boundary: {
            default: undefined,
        },
        noStyle: {
            type: Boolean,
            default: false,
        },
        invertColors: {
            type: Boolean,
            default: false,
        },
        // Should we accept or reject the confirmation when it changes while the
        // confirmation popover is visible?
        acceptOnConfirmChange: {
            type: Boolean,
            default: false,
        },
        // The screen reader message to show on success of the submit action.
        successAlert: {
            type: String,
            default: '',
        },
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    data() {
        const btnId = this.id || `submit-button-${i++}`;
        const disabledId = `${btnId}-disabled-popover`;

        return {
            state: 'default',
            btnId,
            disabledId,
            mounting: true,
            error: null,
            warning: null,
            response: null,
            confirmVisible: false,
            confirmAccepted: false,
            activeElement: Maybe.empty(),
        };
    },

    // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
    mounted() {
        this.mounting = false;
    },

    computed: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        Maybe() {
            return Maybe;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        currentVariant() {
            if (this.invertColors) {
                return 'secondary';
            }
            return {
                default: this.variant,
                pending: this.variant,
                success: 'success',
                error: 'danger',
                warning: 'warning',
            }[this.state];
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        stringifiedError() {
            return utils.getErrorMessage(this.error);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        modalVisible() {
            return this.state === 'pending' && this.confirmVisible && this.confirmInModal;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        isDisabled() {
            let disabled = this.disabled;
            if (typeof disabled !== 'boolean') {
                disabled = disabled.isJust();
            }
            return disabled || this.state !== 'default' || this.confirmVisible;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        innerContainer() {
            if (this.container === 'self') {
                return `#${this.btnId}`;
            }
            return this.container;
        },
    },

    methods: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onClick() {
            if (this.isDisabled) {
                this.showDisabled();
                return;
            }

            if (this.confirm && this.confirm.length > 0) {
                this.showConfirm();
            } else {
                this.doSubmit();
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        showDisabled() {
            this.saveFocus();
            this.$root.$emit('bv::show::popover', this.disabledId);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        hideDisabled() {
            this.$root.$emit('bv::hide::popover', this.disabledId);
            this.restoreFocus();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onHideDisabled() {
            this.$emit('hide-disabled');
            this.clearFocus();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        saveFocus() {
            this.activeElement = Maybe.fromNullable(document.activeElement);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        restoreFocus() {
            this.activeElement.ifJust(el => {
                el.focus();
                this.activeElement = Maybe.empty();
            });
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        clearFocus() {
            this.activeElement = Maybe.empty();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        unwrapEither(value) {
            if (!Either.isEither(value)) {
                return value;
            }
            return value.caseOf({
                Left: x => {
                    throw x;
                },
                Right: x => x,
            });
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        doSubmit() {
            this.state = 'pending';

            let promise = Promise.resolve().then(this.submit);
            if (this.waitAtLeast > 0) {
                promise = utils.waitAtLeast(this.waitAtLeast, promise);
            }

            promise = promise.then(this.unwrapEither);
            promise = promise.then(this.filterSuccess, this.filterError);
            promise.then(this.onSuccess, this.onError);
            promise.then(
                () => this.resetConfirm(false),
                () => this.resetConfirm(false),
            );
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        maybeCall(data, prop) {
            return Promise.all(
                utils.ensureArray(data).map(async dataItem => {
                    if (dataItem == null) {
                        if (!this.$isProduction) {
                            // eslint-disable-next-line
                            console.warn('A null object was returned from the :submit function');
                        }
                    } else if (typeof dataItem[prop] === 'function') {
                        try {
                            await dataItem[prop]();
                        } catch (error) {
                            // eslint-disable-next-line
                            console.error(error);
                        }
                    } else if (!this.$isProduction && Object.hasOwnProperty.call(dataItem, prop)) {
                        // eslint-disable-next-line
                        console.warn(
                            `The property ${prop} was found on ${dataItem}, but was not a function`,
                        );
                    }
                }),
            );
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async onSuccess(data, fromWarning = false) {
            if (!fromWarning) {
                const dataArr = utils.ensureArray(data);
                if (dataArr.some(el => hasWarning(el))) {
                    await this.onWarning(dataArr, data);
                    return;
                }
            }

            await this.maybeCall(data, 'onSuccess');
            this.$emit('success', data);

            // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
            const done = async () => {
                await this.maybeCall(data, 'onAfterSuccess');
                this.$emit('after-success', data);
                this.state = 'default';
            };

            if (this.duration) {
                this.state = 'success';
                await new Promise(resolve => {
                    setTimeout(() => {
                        done().then(resolve);
                    }, this.duration);
                });
            } else if (this.confirm) {
                this.state = 'success';
                await this.$afterRerender().then(done);
            } else {
                await done();
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async onWarning(warning, data) {
            await this.maybeCall(data, 'onWarning');
            this.saveFocus();
            this.$emit('warning', { warning, data });
            this.state = 'warning';

            if (warning instanceof SubmitButtonWarning) {
                this.warning = warning.warning;
            } else {
                this.warning = warning.reduce(
                    (acc, w) => acc.merge(w),
                    api.WarningHeader.fromWarningStr(''),
                );
            }

            this.response = data;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        hideWarning() {
            this.warning = null;
            this.restoreFocus();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        onHideWarning() {
            this.$emit('after-warning');
            this.state = 'default';
            this.warning = null;
            this.clearFocus();

            const res = this.response;
            this.response = null;
            return this.onSuccess(res, true);
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async onError(err) {
            if (err === SubmitButtonCancelled) {
                await this.maybeCall(err, 'onCancel');
                this.$emit('cancel', err);
                this.state = 'default';
            } else if (err instanceof SubmitButtonWarning) {
                await this.onWarning(err, err.data);
            } else if (err === SubmitButtonRetry) {
                await this.maybeCall(err, 'onRetry');
                // In many cases we expect the confirm prop to change, so we only actually retry in
                // the next tick.
                this.$nextTick().then(() => {
                    this.state = 'default';
                    this.onClick();
                });
            } else {
                await this.maybeCall(err, 'onError');
                this.saveFocus();
                this.$emit('error', err);
                this.state = 'error';
                this.error = err;
                if (!IS_PRODUCTION) {
                    // eslint-disable-next-line no-console
                    console.log('SubmitButton error', err);
                }
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async onAfterError() {
            if (this.error != null) {
                await this.maybeCall(this.error, 'onAfterError');
            }
            this.error = null;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async hideError() {
            await this.onAfterError();
            this.restoreFocus();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        async onHideError() {
            await this.onAfterError();
            this.$emit('after-error');
            this.$emit('onAfterError');
            this.state = 'default';
            this.clearFocus();
        },

        /**
         * Shows the confirmation dialog or popover.
         *
         * In case of a popover, the decline button gets immediately focused to make sure
         * keyboard navigation logic is intuitive.
         */
        async showConfirm() {
            this.$emit('show-confirm');
            this.state = 'pending';
            this.confirmVisible = true;

            await this.$afterRerender();
            this.$withRef('popover-decline-accept', el => utils.focusOn(el));
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        resetConfirm(resetState = false) {
            if (!this.confirmAccepted && this.confirmVisible && resetState) {
                this.state = 'default';
                this.$emit('reject-confirm');
            }
            this.confirmVisible = false;
            this.confirmAccepted = false;
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        acceptConfirm() {
            this.$emit('accept-confirm');
            this.confirmVisible = false;
            this.confirmAccepted = true;
            this.doSubmit();
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        isWarningHeader(warning) {
            return warning instanceof api.WarningHeader;
        },
    },

    watch: {
        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        state() {
            if (this.state === 'default') {
                this.$emit('finally');
            }
        },

        // eslint-disable-next-line jsdoc/require-jsdoc -- When touching please add documentation.
        confirm() {
            if (!this.confirm && this.confirmVisible) {
                if (this.acceptOnConfirmChange) {
                    this.acceptConfirm();
                } else {
                    this.resetConfirm(true);
                }
            }
        },
    },

    components: {
        Icon,
        Loader,
    },
};
</script>

<style lang="less" scoped>
.submit-button {
    position: relative;
}

.label.default {
    opacity: 0;

    .state-default & {
        opacity: 1;
    }
}

.label.success,
.label.warning,
.label.error,
.label.pending {
    display: none;
    position: absolute;
    top: 50%;
    left: 50%;
    line-height: 1;
    transform: translate(-50%, -50%);
}

.state-success .label.success,
.state-warning .label.warning,
.state-error .label.error,
.state-pending .label.pending,
.state-default .label.default {
    display: initial;
}

.hide-button {
    float: right;
    cursor: pointer;
    opacity: 0.75;
    margin: 0 0 0.25rem 0.5rem;

    &:hover {
        opacity: 1;
    }
}

.confirm-button {
    flex: 1 1 auto;
}

.success-alert {
    // This ensures that the alert is not visible, but still readable to screen readers.
    // We cannot use display none, or visibility: hidden, since this would remove it from the
    // accessibility tree.
    // Gotten from https://developer.mozilla.org/en-US/docs/Web/Accessibility/ARIA/Roles/alert_role
    clip: rect(0 0 0 0);
    clip-path: inset(50%);
    height: 1px;
    overflow: hidden;
    position: absolute;
    white-space: nowrap;
    width: 1px;
}
</style>

<style lang="less">
.submit-button .loader {
    display: inline-block !important;
}

.submit-button .popover {
    min-width: 15rem;
    opacity: 1;
    background: unset;
}

.submit-button-confirm-modal {
    cursor: initial !important;
    pointer-events: all !important;
}
</style>
